'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgAlarmClock, SvgArrowDown, SvgHome2, SvgMoneybag, SvgSearchBook } from '@common/icon';
import { cn } from '@core/utils';
import { LayoutGroup, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { useAuthStore } from '@/stores/AuthStore';
import { useChatMessages } from '../_hooks/useChatMessages';
import { useChatSession } from '../_hooks/useChatSession';
import { useChatStream } from '../_hooks/useChatStream';
import { useStickToLatestMessage } from '../_hooks/useStickToLatestMessage';
import { createMessage } from '../_utils/message';
import { buildGreetingMessage, buildInitialProfile } from '../_utils/profile';
import { ChatGeneratingIndicator } from './ChatGeneratingIndicator';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage/ChatMessage';
import { IntroMessage } from './IntroMessage';
interface SuggestedPromptItem {
  prompt: string;
  icon: typeof SvgSearchBook;
  toneClassName: string;
}
const SUGGESTED_PROMPTS: readonly SuggestedPromptItem[] = [{
  prompt: '내 조건에 맞는 지원 정책 찾아줘',
  icon: SvgSearchBook,
  toneClassName: 'bg-B-25 text-B-550'
}, {
  prompt: '주거비나 월세 지원이 궁금해',
  icon: SvgHome2,
  toneClassName: 'bg-G-25 text-G-550'
}, {
  prompt: '신청 마감이 가까운 혜택 알려줘',
  icon: SvgAlarmClock,
  toneClassName: 'bg-O-25 text-O-550'
}, {
  prompt: '취업 준비 지원금 추천해줘',
  icon: SvgMoneybag,
  toneClassName: 'bg-P-25 text-P-550'
}];
interface SuggestedPromptsProps {
  onSelect: (prompt: string) => void;
}
const SuggestedPrompts = t0 => {
  const $ = _c(5);
  const {
    onSelect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      paddingBottom: "var(--wello-agent-composer-h, 160px)"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onSelect) {
    t2 = SUGGESTED_PROMPTS.map((suggestion, index) => {
      const Icon = suggestion.icon;
      return <motion.button key={suggestion.prompt} animate={{
        opacity: 1
      }} className="flex items-center gap-12 rounded-medium border border-Gray-200 bg-Gray-white px-14 py-12 text-left transition-colors hover:border-Gray-300 hover:bg-Gray-50" initial={{
        opacity: 0
      }} transition={{
        delay: 0.05 * index,
        duration: 0.2,
        ease: "easeOut"
      }} type="button" onClick={() => onSelect(suggestion.prompt)}><span className={cn("flex size-36 shrink-0 items-center justify-center rounded-pill", suggestion.toneClassName)}><Icon className="size-18" /></span><span className="font-500 text-Gray-800 typo-body4">{suggestion.prompt}</span></motion.button>;
    });
    $[1] = onSelect;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="mx-auto grid w-full max-w-2xl grid-cols-2 gap-8 px-4 mobile-tablet:grid-cols-1 mobile-tablet:px-0" style={t1}>{t2}</div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const Chat = () => {
  const $ = _c(89);
  const myInfo = useAuthStore(_temp);
  let t0;
  if ($[0] !== myInfo?.memberOAuth) {
    t0 = myInfo?.memberOAuth?.toString();
    $[0] = myInfo?.memberOAuth;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const userId = t0;
  const {
    sessionId,
    createSession,
    isCreatingSession
  } = useChatSession();
  const {
    messages,
    setMessages,
    addMessages,
    updateLastMessage
  } = useChatMessages();
  let t1;
  if ($[2] !== addMessages || $[3] !== sessionId || $[4] !== updateLastMessage || $[5] !== userId) {
    t1 = {
      sessionId,
      userId,
      onAddMessages: addMessages,
      onUpdateMessage: updateLastMessage
    };
    $[2] = addMessages;
    $[3] = sessionId;
    $[4] = updateLastMessage;
    $[5] = userId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const {
    isStreaming,
    isToolRunning,
    sendMessage,
    retryLastMessage,
    stopStreaming
  } = useChatStream(t1);
  const [isIntroDone, setIsIntroDone] = useState(false);
  const hasStartedRef = useRef(false);
  const hasPrewarmedRef = useRef(false);
  const hasOpenerSentRef = useRef(false);
  const pendingInputRef = useRef(null);
  let t2;
  if ($[7] !== createSession || $[8] !== myInfo || $[9] !== sessionId || $[10] !== setMessages || $[11] !== userId) {
    t2 = () => {
      if (hasStartedRef.current || sessionId) {
        return;
      }
      hasStartedRef.current = true;
      createSession({
        user_id: userId,
        initial_profile: buildInitialProfile(myInfo)
      }, {
        onError: () => {
          hasStartedRef.current = false;
          setMessages(_temp3);
        }
      });
    };
    $[7] = createSession;
    $[8] = myInfo;
    $[9] = sessionId;
    $[10] = setMessages;
    $[11] = userId;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const startConversation = t2;
  let t3;
  if ($[13] !== myInfo || $[14] !== startConversation) {
    t3 = () => {
      if (myInfo && !hasPrewarmedRef.current) {
        hasPrewarmedRef.current = true;
        startConversation();
      }
    };
    $[13] = myInfo;
    $[14] = startConversation;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  useEffect(t3);
  let t4;
  if ($[16] !== isIntroDone || $[17] !== myInfo || $[18] !== sendMessage || $[19] !== sessionId) {
    t4 = () => {
      if (!sessionId || hasOpenerSentRef.current) {
        return;
      }
      if (!isIntroDone && pendingInputRef.current === null) {
        return;
      }
      hasOpenerSentRef.current = true;
      const pendingInput = pendingInputRef.current;
      pendingInputRef.current = null;
      sendMessage(pendingInput ?? buildGreetingMessage(myInfo), sessionId, {
        displayUserMessage: false
      });
    };
    $[16] = isIntroDone;
    $[17] = myInfo;
    $[18] = sendMessage;
    $[19] = sessionId;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  useEffect(t4);
  let t5;
  if ($[21] !== addMessages || $[22] !== sendMessage || $[23] !== sessionId || $[24] !== startConversation) {
    t5 = input => {
      const trimmedInput = input.trim();
      if (!trimmedInput) {
        return;
      }
      if (sessionId) {
        hasOpenerSentRef.current = true;
        sendMessage(trimmedInput);
        return;
      }
      pendingInputRef.current = trimmedInput;
      addMessages([createMessage("user", {
        content: trimmedInput
      })]);
      startConversation();
    };
    $[21] = addMessages;
    $[22] = sendMessage;
    $[23] = sessionId;
    $[24] = startConversation;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const handleSubmit = t5;
  let t6;
  if ($[26] !== retryLastMessage || $[27] !== setMessages || $[28] !== startConversation) {
    t6 = value => {
      if (value === "retry_session") {
        setMessages(_temp5);
        startConversation();
      }
      if (value === "retry_last") {
        retryLastMessage();
      }
    };
    $[26] = retryLastMessage;
    $[27] = setMessages;
    $[28] = startConversation;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const handleSelectOption = t6;
  let t7;
  if ($[30] !== startConversation) {
    t7 = () => {
      setIsIntroDone(true);
      startConversation();
    };
    $[30] = startConversation;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const handleIntroTypingComplete = t7;
  let t8;
  if ($[32] !== messages) {
    t8 = messages.some(_temp6);
    $[32] = messages;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  const hasUserMessage = t8;
  const isAwaitingSession = isCreatingSession && hasUserMessage;
  let t9;
  if ($[34] !== hasUserMessage || $[35] !== isStreaming || $[36] !== messages) {
    t9 = !isStreaming && messages.length > 0 && !hasUserMessage && !messages.at(-1)?.options;
    $[34] = hasUserMessage;
    $[35] = isStreaming;
    $[36] = messages;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  const shouldShowSuggestions = t9;
  const shouldShowAiStatusIcon = messages.length > 0 || isStreaming;
  const {
    showJumpToBottom,
    jumpToBottom
  } = useStickToLatestMessage(messages);
  let t10;
  if ($[38] !== messages) {
    t10 = messages.at(-1);
    $[38] = messages;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  const lastMessage = t10;
  const isAwaitingFirstToken = (isStreaming || isAwaitingSession) && (!lastMessage || lastMessage.type !== "ai" || !lastMessage.content);
  const [showThinkingLabel, setShowThinkingLabel] = useState(false);
  let t11;
  let t12;
  if ($[40] !== isAwaitingFirstToken) {
    t11 = () => {
      if (!isAwaitingFirstToken) {
        return;
      }
      const timerId = window.setTimeout(() => setShowThinkingLabel(true), 2000);
      return () => {
        window.clearTimeout(timerId);
        setShowThinkingLabel(false);
      };
    };
    t12 = [isAwaitingFirstToken];
    $[40] = isAwaitingFirstToken;
    $[41] = t11;
    $[42] = t12;
  } else {
    t11 = $[41];
    t12 = $[42];
  }
  useEffect(t11, t12);
  const t13 = messages.length === 0;
  const t14 = isStreaming || isAwaitingSession;
  const t15 = myInfo?.nickName ?? "\uD68C\uC6D0";
  let t16;
  if ($[43] !== handleIntroTypingComplete || $[44] !== t13 || $[45] !== t14 || $[46] !== t15) {
    t16 = <IntroMessage isEmptyState={t13} isGenerating={t14} userName={t15} onTypingComplete={handleIntroTypingComplete} />;
    $[43] = handleIntroTypingComplete;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== handleSelectOption || $[49] !== messages || $[50] !== shouldShowAiStatusIcon || $[51] !== shouldShowSuggestions) {
    let t18;
    if ($[53] !== handleSelectOption || $[54] !== messages.length || $[55] !== shouldShowAiStatusIcon || $[56] !== shouldShowSuggestions) {
      t18 = (message_2, index) => <ChatMessage key={message_2.id} isLatest={index === messages.length - 1} message={message_2} withBottomSpacer={index === messages.length - 1 && !shouldShowSuggestions && !shouldShowAiStatusIcon} onSelectOption={handleSelectOption} />;
      $[53] = handleSelectOption;
      $[54] = messages.length;
      $[55] = shouldShowAiStatusIcon;
      $[56] = shouldShowSuggestions;
      $[57] = t18;
    } else {
      t18 = $[57];
    }
    t17 = messages.map(t18);
    $[48] = handleSelectOption;
    $[49] = messages;
    $[50] = shouldShowAiStatusIcon;
    $[51] = shouldShowSuggestions;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[58] !== isAwaitingSession || $[59] !== isStreaming || $[60] !== isToolRunning || $[61] !== shouldShowAiStatusIcon || $[62] !== shouldShowSuggestions || $[63] !== showThinkingLabel) {
    t18 = shouldShowAiStatusIcon && <><ChatGeneratingIndicator isGenerating={isStreaming || isAwaitingSession} label={isToolRunning || showThinkingLabel ? "\uC815\uCC45\uC744 \uCC3E\uACE0 \uC788\uC5B4\uC694" : undefined} />{!shouldShowSuggestions && <div style={{
        height: "var(--wello-agent-composer-h, 160px)"
      }} />}</>;
    $[58] = isAwaitingSession;
    $[59] = isStreaming;
    $[60] = isToolRunning;
    $[61] = shouldShowAiStatusIcon;
    $[62] = shouldShowSuggestions;
    $[63] = showThinkingLabel;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== handleSubmit || $[66] !== shouldShowSuggestions) {
    t19 = shouldShowSuggestions && <SuggestedPrompts onSelect={handleSubmit} />;
    $[65] = handleSubmit;
    $[66] = shouldShowSuggestions;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== t16 || $[69] !== t17 || $[70] !== t18 || $[71] !== t19) {
    t20 = <div aria-live="polite" aria-relevant="additions text" className="scroll-hidden mx-auto flex w-full max-w-3xl flex-1 flex-col gap-24 overflow-y-auto px-16 pb-20 pt-8 mobile-tablet:max-w-none mobile-tablet:gap-20 mobile-tablet:pt-4" role="log">{t16}{t17}{t18}{t19}</div>;
    $[68] = t16;
    $[69] = t17;
    $[70] = t18;
    $[71] = t19;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  const t21 = showJumpToBottom ? "opacity-100" : "pointer-events-none opacity-0";
  let t22;
  if ($[73] !== t21) {
    t22 = cn("fixed left-1/2 z-20 flex size-40 -translate-x-1/2 items-center justify-center rounded-pill border border-Gray-200 bg-Gray-white text-Gray-700 shadow-md transition-opacity hover:bg-Gray-100", t21);
    $[73] = t21;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  let t23;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      bottom: "calc(var(--wello-agent-composer-h, 160px) + 8px)"
    };
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <SvgArrowDown className="size-18" />;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== jumpToBottom || $[78] !== t22) {
    t25 = <button aria-label={"\uB9E8 \uC544\uB798\uB85C"} className={t22} style={t23} type="button" onClick={jumpToBottom}>{t24}</button>;
    $[77] = jumpToBottom;
    $[78] = t22;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== handleSubmit || $[81] !== isAwaitingSession || $[82] !== isStreaming || $[83] !== stopStreaming) {
    t26 = <BottomNavPortal contentClassName="max-w-3xl px-16 pb-20 pt-16 xl:max-w-3xl mobile-tablet:max-w-screen-xl mobile-tablet:px-0 mobile-tablet:pb-0 mobile-tablet:pt-0" wrapperClassName="bg-Gray-white/95 mobile-tablet:bg-Gray-white"><ChatInput isAwaitingSession={isAwaitingSession} isStreaming={isStreaming} onStop={stopStreaming} onSubmit={handleSubmit} /></BottomNavPortal>;
    $[80] = handleSubmit;
    $[81] = isAwaitingSession;
    $[82] = isStreaming;
    $[83] = stopStreaming;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== t20 || $[86] !== t25 || $[87] !== t26) {
    t27 = <LayoutGroup id="wello-agent-chat">{t20}{t25}{t26}</LayoutGroup>;
    $[85] = t20;
    $[86] = t25;
    $[87] = t26;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  return t27;
};
function _temp(state) {
  return state.myInfo;
}
function _temp2(message) {
  return message.type === "user";
}
function _temp3(prev) {
  return [...prev.filter(_temp2), createMessage("ai", {
    content: "\uC77C\uC2DC\uC801\uC778 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.\n\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574\uC8FC\uC138\uC694.",
    textCenter: true,
    options: [{
      value: "retry_session",
      label: "\uB2E4\uC2DC \uC2DC\uB3C4\uD558\uAE30"
    }]
  })];
}
function _temp4(message_0) {
  return message_0.type === "user";
}
function _temp5(prev_0) {
  return prev_0.filter(_temp4);
}
function _temp6(message_1) {
  return message_1.type === "user";
}